import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';
import { ReactComponent as PlusIcon } from '../../assets/plus.svg';
import { ReactComponent as CloseIcon } from '../../assets/close.svg';

const MediaSection = ({ clinicId, onDataChange }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('upload'); // 'upload' or 'manual'

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'announcement', label: 'Announcement' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'educational', label: 'Educational' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'template', label: 'Template' }
  ];

  const [newMedia, setNewMedia] = useState({
    media_id: '',
    media_type: '',
    media_url: '',
    media_name: '',
    media_description: '',
    media_tags: '',
    media_category: 'general'
  });

  useEffect(() => {
    fetchMedia();
  }, [clinicId, searchTerm, filterCategory, filterActive]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clinicService.getClinicMedia(
        clinicId,
        0,
        100,
        searchTerm || null,
        filterActive,
        filterCategory || null
      );
      setMediaItems(response.items || []);
    } catch (err) {
      console.error('Error fetching media:', err);
      setError('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editingMedia) {
      setEditingMedia(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setNewMedia(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill media name from filename if not set
      if (!newMedia.media_name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setNewMedia(prev => ({
          ...prev,
          media_name: nameWithoutExt
        }));
      }
    }
  };

  const handleSaveMedia = async () => {
    try {
      setLoading(true);
      setError(null);

      if (editingMedia) {
        // Update existing media
        await clinicService.updateMedia(clinicId, editingMedia.id, {
          media_name: editingMedia.media_name,
          media_description: editingMedia.media_description,
          media_tags: editingMedia.media_tags,
          media_category: editingMedia.media_category,
          is_active: editingMedia.is_active
        });
        setSuccessMessage('Media updated successfully');
        setEditingMedia(null);
      } else {
        // Add new media
        if (uploadMode === 'upload') {
          // File upload mode
          if (!selectedFile) {
            setError('Please select a file to upload');
            setLoading(false);
            return;
          }
          if (!newMedia.media_name.trim()) {
            setError('Media name is required');
            setLoading(false);
            return;
          }

          await clinicService.uploadMedia(clinicId, selectedFile, {
            media_name: newMedia.media_name,
            media_description: newMedia.media_description,
            media_tags: newMedia.media_tags,
            media_category: newMedia.media_category
          });
          setSuccessMessage('Media uploaded successfully');
        } else {
          // Manual entry mode
          if (!newMedia.media_id.trim()) {
            setError('Media ID is required');
            setLoading(false);
            return;
          }
          if (!newMedia.media_name.trim()) {
            setError('Media name is required');
            setLoading(false);
            return;
          }
          if (!newMedia.media_type.trim()) {
            setError('Media type is required');
            setLoading(false);
            return;
          }
          if (!newMedia.media_url.trim()) {
            setError('Media URL is required');
            setLoading(false);
            return;
          }

          await clinicService.createMedia(clinicId, newMedia);
          setSuccessMessage('Media added successfully');
        }

        setShowAddForm(false);
        setSelectedFile(null);
        setNewMedia({
          media_id: '',
          media_type: '',
          media_url: '',
          media_name: '',
          media_description: '',
          media_tags: '',
          media_category: 'general'
        });
      }

      await fetchMedia();
      if (onDataChange) onDataChange();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving media:', err);
      setError(err.response?.data?.detail || 'Failed to save media. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMedia = (media) => {
    setEditingMedia({ ...media });
    setShowAddForm(false);
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await clinicService.deleteMedia(clinicId, mediaId);
      setSuccessMessage('Media deleted successfully');
      await fetchMedia();
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting media:', err);
      setError(err.response?.data?.detail || 'Failed to delete media. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMedia(null);
    setShowAddForm(false);
    setSelectedFile(null);
    setUploadMode('upload');
    setNewMedia({
      media_id: '',
      media_type: '',
      media_url: '',
      media_name: '',
      media_description: '',
      media_tags: '',
      media_category: 'general'
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Media Library</h3>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            setEditingMedia(null);
          }}
          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
        >
          {showAddForm ? (
            <>
              <CloseIcon className="h-4 w-4 mr-1" />
              Hide Form
            </>
          ) : (
            <>
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Media
            </>
          )}
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingMedia) && (
        <div className="mb-6 p-4 border rounded bg-white">
          <h4 className="text-md font-medium mb-3">
            {editingMedia ? 'Edit Media' : 'Add New Media'}
          </h4>

          {/* Mode selector for adding new media */}
          {!editingMedia && (
            <div className="mb-4 flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="upload"
                  checked={uploadMode === 'upload'}
                  onChange={(e) => setUploadMode(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Upload File</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="manual"
                  checked={uploadMode === 'manual'}
                  onChange={(e) => setUploadMode(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium">Manual Entry</span>
              </label>
            </div>
          )}

          <div className="space-y-3">
            {/* File Upload Mode */}
            {!editingMedia && uploadMode === 'upload' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,video/*,audio/*,application/pdf"
                  className="w-full p-2 border rounded"
                />
                {selectedFile && (
                  <p className="mt-1 text-xs text-green-600">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Supported: Images, Videos, Audio, PDF (Max 16MB)
                </p>
              </div>
            )}

            {/* Manual Entry Mode */}
            {!editingMedia && uploadMode === 'manual' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="media_id"
                    value={newMedia.media_id}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="WhatsApp media ID or bucket path"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The media ID from WhatsApp Business API or GCP bucket path
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="media_type"
                    value={newMedia.media_type}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="e.g., image/jpeg, video/mp4"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    MIME type of the media
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="media_url"
                    value={newMedia.media_url}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="https://..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    URL to access the media file
                  </p>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="media_name"
                value={editingMedia ? editingMedia.media_name : newMedia.media_name}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                placeholder="Display name for the media"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="media_description"
                value={editingMedia ? editingMedia.media_description : newMedia.media_description}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                rows="2"
                placeholder="Describe this media"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <input
                type="text"
                name="media_tags"
                value={editingMedia ? editingMedia.media_tags : newMedia.media_tags}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
                placeholder="Comma-separated tags"
              />
              <p className="mt-1 text-xs text-gray-500">
                e.g., welcome, banner, onboarding
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="media_category"
                value={editingMedia ? editingMedia.media_category : newMedia.media_category}
                onChange={handleInputChange}
                className="w-full p-2 border rounded"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {editingMedia && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={editingMedia.is_active}
                  onChange={(e) => setEditingMedia(prev => ({
                    ...prev,
                    is_active: e.target.checked
                  }))}
                  className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSaveMedia}
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : editingMedia ? 'Update' : 'Add'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search media..."
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="p-2 border rounded"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={filterActive === null ? '' : filterActive.toString()}
            onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
            className="p-2 border rounded"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </div>

      {/* Media List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading media...</div>
      ) : mediaItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No media found. {showAddForm ? 'Fill out the form above to add your first media.' : 'Click "Add Media" to get started.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 border text-left whitespace-nowrap">Preview</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap">Name</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap hidden md:table-cell">Type</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap hidden lg:table-cell">Description</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap hidden md:table-cell">Category</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap hidden lg:table-cell">Tags</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap">Status</th>
                <th className="py-2 px-4 border text-left whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mediaItems.map((media) => (
                <tr key={media.id} className={`border-t ${!media.is_active ? 'bg-gray-50' : ''}`}>
                  {/* Preview */}
                  <td className="py-2 px-4 border">
                    {media.media_type.startsWith('image/') && media.media_url ? (
                      <img
                        src={media.media_url}
                        alt={media.media_name}
                        className="w-16 h-16 object-cover rounded cursor-pointer"
                        onClick={() => window.open(media.media_url, '_blank', 'noopener,noreferrer')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </td>

                  {/* Name */}
                  <td className="py-2 px-4 border">
                    <div className="font-medium text-gray-900">{media.media_name}</div>
                    <div className="text-xs text-gray-500 md:hidden mt-1">
                      <div>{media.media_type}</div>
                      {media.media_description && (
                        <div className="mt-1 text-gray-600">{media.media_description}</div>
                      )}
                    </div>
                  </td>

                  {/* Type */}
                  <td className="py-2 px-4 border hidden md:table-cell">
                    <span className="text-sm text-gray-600">{media.media_type}</span>
                  </td>

                  {/* Description */}
                  <td className="py-2 px-4 border hidden lg:table-cell">
                    <span className="text-sm text-gray-600">
                      {media.media_description || '-'}
                    </span>
                  </td>

                  {/* Category */}
                  <td className="py-2 px-4 border hidden md:table-cell">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                        media.media_category === 'marketing' ? 'bg-blue-100 text-blue-800' :
                        media.media_category === 'announcement' ? 'bg-purple-100 text-purple-800' :
                        media.media_category === 'appointment' ? 'bg-green-100 text-green-800' :
                        media.media_category === 'educational' ? 'bg-yellow-100 text-yellow-800' :
                        media.media_category === 'promotional' ? 'bg-pink-100 text-pink-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {media.media_category}
                    </span>
                  </td>

                  {/* Tags */}
                  <td className="py-2 px-4 border hidden lg:table-cell">
                    <span className="text-sm text-gray-600">
                      {media.media_tags || '-'}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-2 px-4 border">
                    <span
                      className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                        media.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {media.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="py-2 px-4 border">
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => window.open(media.media_url, '_blank', 'noopener,noreferrer')}
                        className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1"
                        title="Open media in new tab"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open
                      </button>
                      <button
                        onClick={() => handleEditMedia(media)}
                        className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteMedia(media.id)}
                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MediaSection;

